import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const fullUrl = (path) => {
    if (!path) return null;
    return path.startsWith('http') ? path : `https://fantasyworldtrpg.cz${path}`;
};

const Messages = () => {
    const formatDate = (dt) => {
        const date = new Date(dt);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const mins = date.getMinutes().toString().padStart(2, '0');
        return `${day}.${month}. ${year} ${hours}:${mins}`;
    };

    const navigate = useNavigate();
    const { id } = useParams();
    const echoUser = localStorage.getItem("echo_user");
    const [conversations, setConversations] = useState([]);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState("");
    const [showPanel, setShowPanel] = useState(false);
    const chatBoxRef = useRef(null);
    const [touchStartX, setTouchStartX] = useState(null);
    const panelRef = useRef();
    const scrolledRef = useRef(false);

    useEffect(() => {
        const fetchConversations = async () => {
            const res = await fetch("https://api.fantasyworldtrpg.cz/echo/chat_list.php", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user: echoUser })
            });
            const data = await res.json();
            if (data.status === "success") {
                setConversations(data.chats);
            }
        };
        if (echoUser) fetchConversations();
    }, [echoUser]);

    useEffect(() => {
        if (!id || !echoUser) return;

        const fetchMessages = async () => {
            try {
                const res = await fetch("https://api.fantasyworldtrpg.cz/echo/chat_messages.php", {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sender: echoUser, target: id })
                });
                const data = await res.json();
                if (data.status === "success") {
                    setMessages(data.messages);
                }
            } catch (err) {
                console.error("Chyba při načítání zpráv:", err);
            }
        };

        fetchMessages(); // první načtení

        const interval = setInterval(fetchMessages, 5000);

        return () => clearInterval(interval);
    }, [id, echoUser]);


    useEffect(() => {
        if (!scrolledRef.current && chatBoxRef.current) {
            chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
            scrolledRef.current = true;
        }
    }, [messages]);

    useEffect(() => {
        scrolledRef.current = false;
    }, [id]);

    const handleSend = async () => {
        if (!messageText.trim()) return;
        const res = await fetch("https://api.fantasyworldtrpg.cz/echo/chat_send.php", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sender: echoUser, target: id, text: messageText })
        });
        const data = await res.json();
        if (data.status === "success") {
            setMessages([...messages, data.message]);
            setMessageText('');
        }
    };

    // Touch handling pro otevření panelu vlevo
    useEffect(() => {
        const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
        const handleTouchEnd = (e) => {
            const deltaX = e.changedTouches[0].clientX - touchStartX;
            if (deltaX > 100) setShowPanel(true);
            if (deltaX < -100) setShowPanel(false);
        };
        const handleClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setShowPanel(false);
            }
        };

        window.addEventListener("touchstart", handleTouchStart);
        window.addEventListener("touchend", handleTouchEnd);
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchend", handleTouchEnd);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [touchStartX]);

    return (
        <div className="container my-5 py-5">
            <div className="row">
                {/* Desktop konverzace */}
                <div className="col-md-4 border-end d-none d-md-block">
                    <h5 className="mb-3">Konverzace</h5>
                    {conversations.map((conv, i) => (
                        <div key={i} className="d-flex align-items-center mb-2" onClick={() => navigate(`/chat/${conv.id}`)} style={{ cursor: 'pointer' }}>
                            <img src={fullUrl(conv.avatar)} alt="" className="rounded-circle me-2" width={40} height={40} style={{ objectFit: 'cover' }} />
                            <div>
                                <strong>{conv.name}</strong><br/>
                                <small className="text-secondary">{conv.last_message}</small>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Mobilní vysouvací panel */}
                <AnimatePresence>
                    {showPanel && (
                        <motion.div
                            className="position-fixed top-0 start-0 h-100 bg-light shadow d-md-none"
                            style={{ width: '80vw', zIndex: 9999 }}
                            initial={{ x: '-100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '-100%' }}
                            transition={{ duration: 0.3 }}
                        >
                            <div ref={panelRef} className="p-3 h-100 overflow-auto">
                                <button className="btn btn-danger btn-sm mb-3" onClick={() => setShowPanel(false)}>Zavřít</button>
                                <h5 className="mb-3">Konverzace</h5>
                                {conversations.map((conv, i) => (
                                    <div key={i} className="d-flex align-items-center mb-2" onClick={() => {
                                        navigate(`/chat/${conv.id}`);
                                        setShowPanel(false);
                                    }} style={{ cursor: 'pointer' }}>
                                        <img src={fullUrl(conv.avatar)} alt="" className="rounded-circle me-2" width={40} height={40} style={{ objectFit: 'cover' }} />
                                        <div>
                                            <strong>{conv.name}</strong><br/>
                                            <small className="text-secondary">{conv.last_message}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Zprávy */}
                <div className="col-md-8">
                    {id ? (
                        <>
                            {(() => {
                                const selectedUser = conversations.find(c => c.id === id);
                                return <h5 className="mb-3">{selectedUser ? selectedUser.name : 'Zprávy'}</h5>;
                            })()}
                            <div className="chat-box mb-3 p-3 border rounded" ref={chatBoxRef} style={{ height: 'calc(100vh - 250px)', overflowY: 'auto', background: '#f9f9f9' }}>
                                {messages.map((msg, index) => (
                                    <div key={index}
                                         className={`mb-3 ${msg.sender === echoUser ? 'text-end' : 'text-start'}`}>
                                        <div
                                            className={`p-2 rounded ${msg.sender === echoUser ? 'bg-danger-subtle text-dark' : 'bg-warning-subtle text-dark'}`}>
                                            {msg.text}
                                        </div>
                                        <small className="text-secondary">{formatDate(msg.dt)}</small>
                                    </div>
                                ))}
                            </div>
                            <div className="d-flex">
                                <input
                                    type="text"
                                    className="form-control me-2"
                                    placeholder="Napiš zprávu..."
                                    value={messageText}
                                    onChange={(e) => setMessageText(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                />
                                <button className="btn btn-danger" onClick={handleSend}><i className="bi bi-send-fill"></i></button>
                            </div>
                        </>
                    ) : (
                        <div className="text-secondary text-center mt-5">Vyber chat vlevo</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Messages;
